import { isEqual } from 'lodash';

import type { RtcConnection } from 'features/sip/interfaces';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';

type MediaStreamFactory = () => Promise<MediaStreamTrack>;

// MediaTrackEntry holds information about a specific type of media track: the actual track if it's already been
// created, a factory function to create it if it hasn't, and the device ID associated with the track. This class is
// designed to allow for lazy loading of tracks, so that they are only created/retrieved when they are needed.
interface MediaTrackEntry {
  deviceId?: any;
  monitoredTrackId?: string;
  track?: MediaStreamTrack;
  trackFactory: MediaStreamFactory;
  isTrackUnavailable: boolean; // This is set to true when we detect that the track is explicitly not available
}

export interface SourceMediaTrack {
  sourceMediaType: SourceMediaTypes;
  track: MediaStreamTrack;
}

export interface SourceMediaTrackDiagnostics {
  deviceId?: any;
  monitoredTrackId?: string;
  isTrackUnavailable: boolean;
  hasTrack: boolean;
  isLocalType: boolean;
  trackDetails?: {
    id: string;
    kind: MediaStreamTrack['kind'];
    label: string;
    readyState: MediaStreamTrack['readyState'];
    enabled: boolean;
    muted: boolean;
  };
}

export class SourceMediaTracks {
  public static StreamsChangedEventName = 'streamsChanged';

  private static instance: SourceMediaTracks;

  private readonly mediaTracks: Record<SourceMediaTypes, MediaTrackEntry>;
  private readonly eventTarget: EventTarget;
  private readonly userData: Map<SourceMediaTypes, any>;
  private readonly pendingTrackRequests: Map<
    SourceMediaTypes,
    Promise<MediaStreamTrack>
  >;
  private videoSettings?: MediaTrackConstraints;

  private getUserMedia: (
    constraints?: MediaStreamConstraints
  ) => Promise<MediaStream>;

  private constructor() {
    this.mediaTracks = SourceMediaTracks.createDefaultStreams();
    this.eventTarget = new EventTarget();
    this.userData = new Map();
    this.pendingTrackRequests = new Map();

    this.getUserMedia = (constraints?: MediaStreamConstraints) => {
      // Automatically merge videoSettings into video constraints if present and defined
      if (
        this.videoSettings &&
        constraints?.video &&
        typeof constraints.video === 'object'
      ) {
        return navigator.mediaDevices.getUserMedia({
          ...constraints,
          video: {
            ...this.videoSettings,
            ...constraints.video,
          },
        });
      }
      return navigator.mediaDevices.getUserMedia(constraints);
    };
  }

  // Note: forceNewInstance should ONLY be used in testing scenarios
  public static getInstance(forceNewInstance = false): SourceMediaTracks {
    if (!SourceMediaTracks.instance || forceNewInstance) {
      SourceMediaTracks.instance = new SourceMediaTracks();
    }
    return SourceMediaTracks.instance;
  }

  public overrideDependencies(
    getUserMedia: (constraints?: MediaStreamConstraints) => Promise<MediaStream>
  ) {
    this.getUserMedia = getUserMedia;
  }

  public async getTrack(
    mediaType: SourceMediaTypes
  ): Promise<MediaStreamTrack> {
    const streamEntry = this.mediaTracks[mediaType];

    // Fast path: return the live cached track
    if (streamEntry.track) {
      if (streamEntry.track.readyState === 'ended') {
        console.log(
          'Debug: Detected ended track, clearing cache for mediaType:',
          mediaType
        );
        streamEntry.track = undefined;
        // Notify observers immediately so they can react to the ended track,
        // even if creating the replacement track below fails.
        this.dispatchTracksChanged();
        // Note: Do NOT return here — execution continues below to create fresh track
      } else {
        return streamEntry.track;
      }
    }

    // No cached track (or we just cleared an ended one above) — create fresh track

    // Deduplicate concurrent requests: if a getTrack() call for this media type is already
    // in flight, return the same promise to prevent duplicate getUserMedia calls and
    // orphaned tracks that would leak camera/mic resources.
    const pendingRequest = this.pendingTrackRequests.get(mediaType);
    if (pendingRequest) {
      return pendingRequest;
    }

    const trackPromise = (async () => {
      const createMonitoredTrack = async (trackFactory: MediaStreamFactory) => {
        const checkClearTrack = () => {
          if (streamEntry.monitoredTrackId === monitoredTrackId) {
            streamEntry.track = undefined;
            this.dispatchTracksChanged();
          }
        };

        const monitoredTrackId = Math.random().toString(36).substring(8);
        streamEntry.monitoredTrackId = monitoredTrackId;

        const track = await trackFactory();

        // Intercept original stop method to log when track is stopped
        const originalStop = track.stop;

        // Need to intercept the stop method as well as handle the onended event because the onended event is not fired
        // when the track is stopped by calling stop() directly
        track.stop = () => {
          console.log(`Detected track stopped for mediaType: ${mediaType}`);

          originalStop.call(track);
          checkClearTrack();
        };

        track.onended = () => {
          console.log(`Detected track ended for mediaType: ${mediaType}`);
          checkClearTrack();
        };

        return track;
      };

      const track = await createMonitoredTrack(streamEntry.trackFactory);
      streamEntry.track = track;
      this.dispatchTracksChanged();

      return track;
    })();

    this.pendingTrackRequests.set(mediaType, trackPromise);

    try {
      return await trackPromise;
    } finally {
      this.pendingTrackRequests.delete(mediaType);
    }
  }

  public isTrackAvailable(mediaType: SourceMediaTypes) {
    const streamEntry = this.mediaTracks[mediaType];
    if (streamEntry.track) {
      return true;
    }

    // If the track is not available, check if we've explicitly detected that it's explicitly unavailable
    // Note that there is very small window of time where the track could be unavailable but we haven't detected it yet
    return !streamEntry.isTrackUnavailable;
  }

  public setDeafRtcConnection(rtcConnection: RtcConnection | undefined) {
    let audioTrack: MediaStreamTrack | undefined = undefined;
    let videoTrack: MediaStreamTrack | undefined = undefined;

    if (rtcConnection) {
      const [audioReceiver, videoReceiver] = rtcConnection.getReceivers();
      audioTrack = audioReceiver?.track;
      videoTrack = videoReceiver?.track;
    }

    let tracksChanged = false;

    if (
      audioTrack?.id !== this.mediaTracks[SourceMediaTypes.DEAF_AUDIO].deviceId
    ) {
      this.processRemoteTrackChanged(
        SourceMediaTypes.DEAF_AUDIO,
        audioTrack?.id,
        audioTrack,
        false
      );
      tracksChanged = true;
    }

    if (
      videoTrack?.id !== this.mediaTracks[SourceMediaTypes.DEAF_VIDEO].deviceId
    ) {
      this.processRemoteTrackChanged(
        SourceMediaTypes.DEAF_VIDEO,
        videoTrack?.id,
        videoTrack,
        false
      );
      tracksChanged = true;
    }

    if (tracksChanged) {
      this.dispatchTracksChanged();
    }
  }

  public setRemoteTrack(
    mediaType: SourceMediaTypes,
    track: MediaStreamTrack | undefined
  ): void {
    if (SourceMediaTracks.isLocalMediaType(mediaType)) {
      throw new Error(
        `Cannot set remote stream for local mediaType: ${mediaType}`
      );
    }
    this.processRemoteTrackChanged(mediaType, undefined, track);
  }

  public setCameraId(cameraId: any) {
    if (cameraId === this.mediaTracks[SourceMediaTypes.INTERP_VIDEO].deviceId) {
      return;
    }

    this.processLocalDeviceChanged(SourceMediaTypes.INTERP_VIDEO, cameraId, {
      video: {
        deviceId: cameraId ? { exact: cameraId } : undefined,
      },
      audio: false,
    });
  }

  public setMicrophoneId(microphoneId: any) {
    if (
      microphoneId === this.mediaTracks[SourceMediaTypes.INTERP_AUDIO].deviceId
    ) {
      return;
    }

    this.processLocalDeviceChanged(
      SourceMediaTypes.INTERP_AUDIO,
      microphoneId,
      {
        video: false,
        audio: {
          deviceId: microphoneId ? { exact: microphoneId } : undefined,
        },
      }
    );
  }

  public async getActiveTracks(): Promise<SourceMediaTrack[]> {
    return Object.entries(this.mediaTracks)
      .filter(([, entry]) => entry.track)
      .map(([sourceMediaType, entry]) => ({
        sourceMediaType: sourceMediaType as SourceMediaTypes,
        track: entry.track!,
      }));
  }

  public addStreamsChangedListener(
    listener: EventListenerOrEventListenerObject
  ): void {
    this.eventTarget.addEventListener(
      SourceMediaTracks.StreamsChangedEventName,
      listener
    );
  }

  public removeStreamsChangedListener(
    listener: EventListenerOrEventListenerObject
  ): void {
    this.eventTarget.removeEventListener(
      SourceMediaTracks.StreamsChangedEventName,
      listener
    );
  }

  public getUserData(sourceMediaType: SourceMediaTypes): any {
    return this.userData.get(sourceMediaType);
  }

  public setUserData(sourceMediaType: SourceMediaTypes, userData: any): void {
    if (!userData) {
      this.userData.delete(sourceMediaType);
      return;
    }
    this.userData.set(sourceMediaType, userData);
  }

  public setVideoSettings(settings: MediaTrackConstraints): void {
    // Early return if settings haven't changed
    if (this.videoSettings && isEqual(this.videoSettings, settings)) {
      return;
    }
    this.videoSettings = settings;
  }

  public getTrackDiagnostics(): Record<
    SourceMediaTypes,
    SourceMediaTrackDiagnostics
  > {
    const diagnosticsEntries = Object.entries(this.mediaTracks).map(
      ([sourceMediaType, entry]) => {
        const trackDetails = entry.track
          ? {
              id: entry.track.id,
              kind: entry.track.kind,
              label: entry.track.label,
              readyState: entry.track.readyState,
              enabled: entry.track.enabled,
              muted: entry.track.muted,
            }
          : undefined;

        return [
          sourceMediaType as SourceMediaTypes,
          {
            deviceId: entry.deviceId,
            monitoredTrackId: entry.monitoredTrackId,
            isTrackUnavailable: entry.isTrackUnavailable,
            hasTrack: Boolean(entry.track),
            isLocalType: SourceMediaTracks.isLocalMediaType(
              sourceMediaType as SourceMediaTypes
            ),
            trackDetails,
          } as SourceMediaTrackDiagnostics,
        ];
      }
    );

    return Object.fromEntries(diagnosticsEntries) as Record<
      SourceMediaTypes,
      SourceMediaTrackDiagnostics
    >;
  }

  private dispatchTracksChanged() {
    const detail = { streams: this.mediaTracks };
    const event = new CustomEvent(SourceMediaTracks.StreamsChangedEventName, {
      detail,
    });
    this.eventTarget.dispatchEvent(event);
  }

  private processRemoteTrackChanged(
    mediaType: SourceMediaTypes,
    deviceId: any,
    track: MediaStreamTrack | undefined,
    dispatchEvent = true
  ) {
    this.mediaTracks[mediaType] = SourceMediaTracks.createValueStreamEntry(
      deviceId,
      track
    );

    if (dispatchEvent) {
      this.dispatchTracksChanged();
    }
  }

  private processLocalDeviceChanged(
    mediaType: SourceMediaTypes,
    deviceId: any,
    newConstraints: MediaStreamConstraints | undefined,
    dispatchEvent = true
  ) {
    // Stop the old cached track before replacing the entry — prevents the old
    // camera/mic from staying open after a device switch.
    const oldEntry = this.mediaTracks[mediaType];
    if (oldEntry.track?.readyState === 'live') {
      console.log(
        `Stopping old ${mediaType} track (device changed from ${oldEntry.deviceId} to ${deviceId})`
      );
      oldEntry.monitoredTrackId = undefined;
      oldEntry.track.stop();
    }

    this.mediaTracks[mediaType] = newConstraints
      ? this.createLocalDeviceStreamEntry(deviceId, newConstraints)
      : SourceMediaTracks.createValueStreamEntry(undefined, undefined);

    if (dispatchEvent) {
      this.dispatchTracksChanged();
    }
  }

  private static createDefaultStreams(): Record<
    SourceMediaTypes,
    MediaTrackEntry
  > {
    const streams: Partial<Record<SourceMediaTypes, MediaTrackEntry>> = {};

    for (const key of Object.values(SourceMediaTypes)) {
      streams[key] = SourceMediaTracks.createValueStreamEntry(
        undefined,
        undefined
      );
    }

    return streams as Record<SourceMediaTypes, MediaTrackEntry>;
  }

  // Create a MediaTrackEntry representing a track that has already been created and is explicitly specified by the
  // caller
  private static createValueStreamEntry(
    deviceId: any,
    track: MediaStreamTrack | undefined
  ): MediaTrackEntry {
    return {
      deviceId,
      track: track,
      trackFactory: () =>
        track
          ? Promise.resolve(track)
          : Promise.reject(new Error('No media track is currently available')),
      isTrackUnavailable: track === undefined,
    };
  }

  // Create a MediaTrackEntry representing a device on the local machine (e.g. camera, microphone)
  private createLocalDeviceStreamEntry(
    deviceId: any,
    constraints: MediaStreamConstraints
  ): MediaTrackEntry {
    const entry = {
      deviceId,
      isTrackUnavailable: false,
      trackFactory: async () => {
        try {
          const stream = await this.getUserMedia(constraints);
          const track = stream.getTracks()[0];

          // Mark track as centrally managed so external consumers can respect lifecycle without
          // requiring a direct dependency on SourceMediaTracks
          SourceMediaTracks.markAsManaged(track);

          return track;
        } catch (error) {
          entry.isTrackUnavailable = true;
          throw error;
        }
      },
    };

    return entry;
  }

  // Helper methods
  private static isLocalMediaType(mediaType: SourceMediaTypes) {
    return [
      SourceMediaTypes.INTERP_VIDEO,
      SourceMediaTypes.INTERP_AUDIO,
    ].includes(mediaType);
  }

  private static markAsManaged(track: MediaStreamTrack): void {
    Object.defineProperty(track, 'mercuryManaged', {
      value: true,
      configurable: false,
      enumerable: false,
      writable: false,
    });
  }
}
